/**
 * useResolvedRunIdentity — disk-backed (trial, session) fallback.
 *
 * Ported 2026-04-24 from the classic UI's ``SectionNavigator`` L71-102
 * pattern, verbatim in intent, to close a Variant C parity gap.
 *
 * ## Why this hook exists
 *
 * Chat requests need ``trial`` + ``session`` so the backend's
 * provenance tools can resolve the right on-disk run directory.
 * Variant C's primary source for those values is the ``run_metadata``
 * SSE event (consumed by ``useVariantCRun`` → ``identity``). That
 * event fires for LIVE runs, emitted by
 * ``gate_runner_wrapper.py::_emit_run_metadata``.
 *
 * The SANDBOX runner (``src/csr_sim/sandbox/fixture_runner.py``) does
 * NOT emit ``run_metadata``. So a fresh ``/draft`` in sandbox leaves
 * ``identity`` null → chat sends ``trial=undefined, session=undefined``
 * → backend's provenance lookup can't find the ledger, even though
 * it's on disk at ``output/logs/DEMO_CER/demo_session/``.
 *
 * The classic UI solved this by calling ``/api/latest-sections/<name>``
 * (with ``"auto"`` fallback) on mount. The endpoint scans
 * ``output/logs/`` for the most recent ``checkpoint_Generate.json``
 * and returns ``{trial, session_id}`` derived from the directory names.
 * In sandbox that resolves to ``DEMO_CER / demo_session``.
 *
 * Variant C now mirrors that.
 *
 * # DECISION (2026-04-24): port the classic-UI mechanism rather than
 * #   invent a new one (e.g. having fixture_runner.py emit
 * #   ``run_metadata``).
 * # WHY: the classic UI's mechanism is proven in production for
 * #      sandbox + live + reopen-pane flows. Inventing a new emit
 * #      path would create two mechanisms to keep in sync; porting
 * #      keeps one source of truth (the ``/latest-sections/`` endpoint).
 * #      Also no backend change required — the endpoint already exists.
 * # DATE: 2026-04-24
 *
 * ## Precedence
 *
 * This is a FALLBACK. The caller (``VariantCShell``) should prefer
 * any identity that came via ``run_metadata`` SSE — that value is
 * FRESHER (emitted at the start of the active run) and REAL-TIME
 * (not dependent on disk state). Only when SSE identity is null
 * should the resolver's values be used.
 *
 * ## SRP
 *
 * One concern: async-resolve a (trial, session) pair from the
 * latest-sections endpoint. No state beyond the resolved values
 * and a first-fire guard. Pure from the caller's POV.
 */

import { useEffect, useRef, useState } from "react";
import { getLatestSections } from "../../api/client";

export interface ResolvedRunIdentity {
  /** Trial name derived from the most recent checkpoint's parent
   *  directory. Null before resolution, or if no runs exist on disk. */
  trial: string | null;
  /** Session id derived from the checkpoint's immediate parent.
   *  Null before resolution, or if no runs exist on disk. */
  session: string | null;
}

/**
 * Resolve a (trial, session) pair by asking the backend for the most
 * recent checkpoint on disk. Fires ONCE on mount — re-running is
 * unnecessary because the disk layout doesn't change during a pane
 * session in ways the caller can't handle itself (a new run emits
 * ``run_metadata`` which supersedes this fallback anyway).
 *
 * @param projectName Optional hint for the exact-match lookup. When
 *        the hint doesn't resolve (typical in sandbox, where Variant
 *        C synthesises a project name like ``vc-CER-...`` that never
 *        lives on disk), the resolver falls back to ``"auto"`` which
 *        returns the most recent checkpoint across ALL trials.
 */
export function useResolvedRunIdentity(projectName?: string | null): ResolvedRunIdentity {
  const [resolved, setResolved] = useState<ResolvedRunIdentity>({
    trial: null,
    session: null,
  });
  // One-shot guard — the classic UI uses the same pattern (ref, not
  // deps) because ``getLatestSections`` is heavy enough that we don't
  // want it firing per-prop-change. If a caller genuinely needs a
  // re-resolve on project-name change, they can remount the consumer.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        // Strategy 1: try the exact project name the shell synthesised.
        // In live runs this matches the on-disk trial; in sandbox it
        // doesn't (fixture source is DEMO_CER, not the synthesised
        // vc-CER-... name).
        let latest = null;
        if (projectName) {
          try {
            latest = await getLatestSections(projectName);
          } catch {
            // Swallow — fall through to auto-discovery.
          }
        }

        // Strategy 2: auto-discovery across all trials. For sandbox
        // runs this finds DEMO_CER/demo_session because that's the
        // most recent checkpoint.
        if (!latest?.sections?.length) {
          try {
            latest = await getLatestSections("auto");
          } catch {
            return;
          }
        }

        if (cancelled || !latest) return;

        setResolved({
          trial: latest.trial ?? null,
          session: latest.session_id ?? null,
        });
      } catch {
        // Degrade silently — chat still works without provenance;
        // classic UI follows the same degradation. The writer-facing
        // signal is a "no provenance record" reply from the agent,
        // which is more honest than a hard error.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectName]);

  return resolved;
}
